import React, { useEffect, useState, useMemo } from 'react';
import { Table, Card, Button, Space, Typography, Input, Popconfirm, message, Modal, Form, Row, Col } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, EyeOutlined, EditOutlined, ReloadOutlined, ShopOutlined, PhoneOutlined, GlobalOutlined, CalendarOutlined } from '@ant-design/icons';
import StatCard from '../components/StatCard';
import useProviderStore from '../store/providerStore';

const { Title } = Typography;

const ProvidersPage = () => {
  const { providers, loading, pagination, fetchProviders, deleteProvider, createProvider, updateProvider } = useProviderStore();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleDelete = async (id) => {
    try {
      await deleteProvider(id);
      message.success("O'chirildi");
      fetchProviders();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingProvider) {
        await updateProvider(editingProvider.id, values);
        message.success('Yangilandi');
      } else {
        await createProvider(values);
        message.success("Qo'shildi");
      }
      setModalOpen(false);
      setEditingProvider(null);
      form.resetFields();
      fetchProviders();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const openEdit = (record) => {
    setEditingProvider(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const openDetail = (record) => {
    setDetailData(record);
    setDetailOpen(true);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60, sorter: (a, b) => a.id - b.id },
    { title: 'Nomi', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: 'Telefon', dataIndex: 'phone', key: 'phone' },
    { title: 'Manzil', dataIndex: 'address', key: 'address', ellipsis: true },
    { title: 'STIR', dataIndex: 'stir', key: 'stir' },
    {
      title: 'Sana',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => d ? new Date(d).toLocaleDateString('uz-UZ') : '-',
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} onClick={() => openDetail(record)} />
          <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filtered = providers?.filter((p) =>
    (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.phone || '').includes(search) ||
    (p.address || '').toLowerCase().includes(search.toLowerCase())
  );

  const stats = useMemo(() => {
    const list = providers || [];
    const today = new Date().toISOString().slice(0, 10);
    return {
      total: list.length,
      withPhone: list.filter(p => p.phone).length,
      withWebsite: list.filter(p => p.website).length,
      today: list.filter(p => (p.created_at || '').slice(0, 10) === today).length,
    };
  }, [providers]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Title level={3} style={{ margin: 0 }}>Provayderlar</Title>
        <Space wrap>
          <Input
            placeholder="Qidirish..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchProviders()}>Yangilash</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingProvider(null); form.resetFields(); setModalOpen(true); }}>
            Qo'shish
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Jami provayderlar" value={stats.total} icon={<ShopOutlined />} color="#4f46e5" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Telefon mavjud" value={stats.withPhone} icon={<PhoneOutlined />} color="#13c2c2" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Veb-sayt mavjud" value={stats.withWebsite} icon={<GlobalOutlined />} color="#722ed1" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Bugun qo'shilgan" value={stats.today} icon={<CalendarOutlined />} color="#52c41a" loading={loading} />
        </Col>
      </Row>

      <Card style={{ borderRadius: 12 }}>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Jami: ${total}`,
          }}
          onChange={(pg) => fetchProviders({ page: pg.current, per_page: pg.pageSize })}
        />
      </Card>

      <Modal
        title={editingProvider ? 'Provayderni tahrirlash' : "Yangi provayder qo'shish"}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingProvider(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Saqlash"
        cancelText="Bekor qilish"
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Nomi" rules={[{ required: true, message: 'Nomini kiriting' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Telefon">
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Manzil">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Tavsif">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="stir" label="STIR">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="website" label="Veb-sayt">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Provayder tafsilotlari"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={600}
      >
        {detailData && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div><strong>ID:</strong> {detailData.id}</div>
            <div><strong>Nomi:</strong> {detailData.name}</div>
            <div><strong>Telefon:</strong> {detailData.phone || '-'}</div>
            <div><strong>Manzil:</strong> {detailData.address || '-'}</div>
            <div><strong>Tavsif:</strong> {detailData.description || '-'}</div>
            <div><strong>STIR:</strong> {detailData.stir || '-'}</div>
            <div><strong>Veb-sayt:</strong> {detailData.website || '-'}</div>
            <div><strong>Viloyat ID:</strong> {detailData.region_id || '-'}</div>
            <div><strong>Tuman ID:</strong> {detailData.district_id || '-'}</div>
            <div><strong>Yaratilgan:</strong> {detailData.created_at ? new Date(detailData.created_at).toLocaleString('uz-UZ') : '-'}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProvidersPage;
